import type { SortKey } from "./types";

export interface SelectOption<T extends string = string> {
  value: T;
  label: string;
}

export interface CategoryPack {
  key: string;
  label: string;
  category: string;
  sizeClass: string;
  topicScore: number;
  commissionNote: string;
  recommendationReason: string;
  minRating: number;
  minReviews: number;
  minPrice: number;
  maxPrice: number;
  excludeTerms: string[];
  contentAngles: string[];
  seedQueries: string[];
}

export const categories: SelectOption[] = [
  { value: "all", label: "全部分類" },
  { value: "fashion", label: "Fashion" },
  { value: "beauty", label: "Beauty" },
  { value: "homeKitchen", label: "Home & Kitchen" },
  { value: "electronics", label: "Electronics" },
  { value: "luggage", label: "Luggage" },
  { value: "sports", label: "Sports & Outdoors" },
  { value: "pets", label: "Pet Supplies" },
  { value: "tools", label: "Tools" },
  { value: "baby", label: "Baby" },
  { value: "books", label: "Books" }
];

export const sizeClasses: SelectOption[] = [
  { value: "any", label: "不限尺寸/規格" },
  { value: "compact", label: "Compact / Travel" },
  { value: "standard", label: "Standard / Daily" },
  { value: "large", label: "Large / Family" },
  { value: "bulk", label: "Bundle / Value Pack" }
];

export const sortOptions: Array<SelectOption<SortKey>> = [
  { value: "relevance", label: "Amazon 關聯度" },
  { value: "confidence", label: "本工具信心分數" },
  { value: "rating_desc", label: "評分高到低" },
  { value: "reviews_desc", label: "評論數高到低" },
  { value: "price_asc", label: "價格低到高" },
  { value: "price_desc", label: "價格高到低" },
  { value: "title_az", label: "標題 A-Z" }
];

export const categoryIndex: Record<string, string> = {
  all: "aps",
  fashion: "fashion",
  beauty: "beauty",
  homeKitchen: "kitchen",
  electronics: "electronics",
  luggage: "fashion-luggage",
  sports: "sporting",
  pets: "pets",
  tools: "tools",
  baby: "baby-products",
  books: "stripbooks"
};

export const sizeHints: Record<string, string[]> = {
  any: [],
  compact: ["compact", "mini", "travel", "portable", "small"],
  standard: ["standard", "regular", "medium", "daily"],
  large: ["large", "xl", "oversized", "family", "capacity"],
  bulk: ["bulk", "pack", "set", "bundle", "value"]
};

export const collectionProfiles = [
  { value: "quick", label: "Quick / 5 queries", queryCount: 5 },
  { value: "standard", label: "Standard / 12 queries", queryCount: 12 },
  { value: "deep", label: "Deep / 25 queries", queryCount: 25 }
] as const;

export type CollectionProfile = (typeof collectionProfiles)[number]["value"];

export const categoryPacks: CategoryPack[] = [
  {
    key: "travel_luggage",
    label: "Luggage / Travel Organization",
    category: "luggage",
    sizeClass: "any",
    topicScore: 92,
    commissionNote: "US Luggage: 4%",
    recommendationReason: "legacy A 級集中、抓取穩定，適合 travel capsule、packing、work travel 內容。",
    minRating: 4.2,
    minReviews: 150,
    minPrice: 18,
    maxPrice: 320,
    excludeTerms: ["replacement wheel", "lock only", "tag only"],
    contentAngles: ["carry-on packing", "business travel", "family travel", "capsule wardrobe"],
    seedQueries: [
      "best carry on luggage",
      "luggage set",
      "packing cubes set",
      "underseat carry on bag",
      "travel garment bag",
      "weekender bag for women",
      "travel backpack carry on",
      "hard shell suitcase",
      "toiletry bag travel",
      "shoe bags for travel",
      "compression packing cubes",
      "luggage organizer set",
      "lightweight checked luggage",
      "personal item travel bag",
      "travel jewelry organizer",
      "passport holder travel wallet",
      "luggage scale",
      "travel laundry bag",
      "carry on duffel bag",
      "laptop backpack travel",
      "rolling garment bag",
      "travel makeup bag",
      "packing cube compression set",
      "weekender bag with shoe compartment",
      "travel document organizer"
    ]
  },
  {
    key: "beauty_grooming",
    label: "Beauty & Grooming / Skincare",
    category: "beauty",
    sizeClass: "standard",
    topicScore: 88,
    commissionNote: "US Beauty: 3%, Luxury Beauty: 10%",
    recommendationReason: "內容選題強、搜尋需求穩定，可做 routine、travel kit、敏感肌選題。",
    minRating: 4.3,
    minReviews: 500,
    minPrice: 8,
    maxPrice: 120,
    excludeTerms: ["prescription", "medical grade", "for disease"],
    contentAngles: ["daily routine", "travel kit", "sensitive skin", "grooming basics"],
    seedQueries: [
      "hydrating facial cleanser",
      "ceramide moisturizer",
      "daily facial sunscreen",
      "retinol serum",
      "hyaluronic acid serum",
      "vitamin c serum",
      "eye cream",
      "makeup remover balm",
      "travel skincare set",
      "body lotion for dry skin",
      "shampoo for dry hair",
      "hair mask",
      "dry shampoo",
      "facial cleansing balm",
      "men grooming kit",
      "electric facial cleansing brush",
      "lip balm pack",
      "hand cream",
      "cuticle oil",
      "makeup brush set",
      "travel toiletry bottles",
      "body wash sensitive skin",
      "beard trimmer",
      "scalp massager",
      "microfiber hair towel"
    ]
  },
  {
    key: "home_kitchen_coffee",
    label: "Home & Kitchen / Coffee / Organization",
    category: "homeKitchen",
    sizeClass: "standard",
    topicScore: 84,
    commissionNote: "US Kitchen: 4.5%, Home: 3%",
    recommendationReason: "高 AOV 與比較文潛力，適合 small apartment、coffee bar、home reset。",
    minRating: 4.2,
    minReviews: 250,
    minPrice: 15,
    maxPrice: 550,
    excludeTerms: ["replacement part", "manual only", "filter only"],
    contentAngles: ["small apartment", "coffee bar", "hosting", "home organization"],
    seedQueries: [
      "espresso machine for home",
      "coffee grinder burr",
      "gooseneck kettle",
      "milk frother",
      "single serve coffee maker",
      "air fryer",
      "instant pot",
      "countertop organizer",
      "closet organizer",
      "drawer organizer",
      "under sink organizer",
      "food storage containers",
      "glass meal prep containers",
      "compact dish rack",
      "kitchen scale",
      "knife set",
      "cutting board set",
      "bedding set queen",
      "floor lamp",
      "cordless vacuum",
      "robot vacuum",
      "humidifier bedroom",
      "air purifier bedroom",
      "laundry hamper",
      "entryway shoe rack"
    ]
  },
  {
    key: "outdoor_fitness",
    label: "Outdoor / Fitness / Compact Home Gym",
    category: "sports",
    sizeClass: "standard",
    topicScore: 80,
    commissionNote: "US Sports/Outdoors: 3%",
    recommendationReason: "legacy 成功率高，適合 apartment gym、beginner fitness、camping checklist。",
    minRating: 4.2,
    minReviews: 200,
    minPrice: 10,
    maxPrice: 400,
    excludeTerms: ["replacement cap", "single screw", "manual"],
    contentAngles: ["home gym", "beginner fitness", "camping basics", "mobility recovery"],
    seedQueries: [
      "adjustable dumbbells",
      "resistance bands set",
      "yoga mat",
      "kettlebell",
      "weight bench",
      "pull up bar",
      "massage gun",
      "foam roller",
      "jump rope",
      "pilates ring",
      "camping chair",
      "camping lantern",
      "cooler backpack",
      "hiking daypack",
      "water bottle insulated",
      "portable hammock",
      "sleeping pad camping",
      "camping cookware set",
      "trekking poles",
      "pickleball paddle set",
      "bike phone mount",
      "running belt",
      "ankle weights",
      "exercise mat thick",
      "balance board"
    ]
  },
  {
    key: "pet_supplies",
    label: "Pet Supplies",
    category: "pets",
    sizeClass: "standard",
    topicScore: 78,
    commissionNote: "US Pets: 3%, Pet Food & Supplies bonus event may apply",
    recommendationReason: "可建立新內容池，適合 apartment pet、travel pet、clean home with pets。",
    minRating: 4.2,
    minReviews: 300,
    minPrice: 8,
    maxPrice: 220,
    excludeTerms: ["prescription", "veterinary diet", "medicine"],
    contentAngles: ["apartment pets", "pet travel", "clean home", "starter kit"],
    seedQueries: [
      "dog travel bag",
      "cat water fountain",
      "dog car seat cover",
      "pet hair remover",
      "automatic cat feeder",
      "dog harness",
      "cat litter mat",
      "pet grooming brush",
      "dog bed washable",
      "cat tree",
      "pet carrier airline approved",
      "dog puzzle toys",
      "slow feeder dog bowl",
      "cat scratching post",
      "pet stain remover",
      "dog leash",
      "portable dog water bottle",
      "cat backpack carrier",
      "dog raincoat",
      "pet camera",
      "dog nail grinder",
      "cat toys variety pack",
      "pet blanket washable",
      "dog poop bags",
      "cat grooming glove"
    ]
  },
  {
    key: "tools_home_improvement",
    label: "Tools / Home Improvement",
    category: "tools",
    sizeClass: "standard",
    topicScore: 75,
    commissionNote: "US Tools/Home Improvement: 3%",
    recommendationReason: "問題導向搜尋強，適合 renter-friendly、DIY starter、small repair guides。",
    minRating: 4.2,
    minReviews: 180,
    minPrice: 12,
    maxPrice: 350,
    excludeTerms: ["replacement blade only", "bit only", "manual"],
    contentAngles: ["DIY starter", "renter-friendly", "garage organization", "small repair"],
    seedQueries: [
      "cordless drill",
      "tool set for home",
      "laser level",
      "stud finder",
      "screwdriver set",
      "socket set",
      "tape measure",
      "work gloves",
      "tool bag",
      "garage organizer",
      "cordless screwdriver",
      "utility knife",
      "paint sprayer",
      "ladder telescoping",
      "power tool combo kit",
      "wall anchors",
      "caulking gun",
      "home repair kit",
      "work light rechargeable",
      "clamp set",
      "pliers set",
      "wire stripper",
      "level tool",
      "sanding block",
      "tool box organizer"
    ]
  },
  {
    key: "baby_nursery",
    label: "Baby / Nursery",
    category: "baby",
    sizeClass: "standard",
    topicScore: 72,
    commissionNote: "US Baby: 3%",
    recommendationReason: "需求穩定，可做 nursery checklist 與 travel with baby，但需保守處理安全宣稱。",
    minRating: 4.4,
    minReviews: 500,
    minPrice: 8,
    maxPrice: 350,
    excludeTerms: ["medicine", "medical", "unsafe sleep", "crib bumper"],
    contentAngles: ["nursery checklist", "travel with baby", "compact apartment", "feeding setup"],
    seedQueries: [
      "baby travel bag",
      "diaper backpack",
      "baby monitor",
      "bottle warmer",
      "baby sound machine",
      "nursery organizer",
      "baby play mat",
      "stroller organizer",
      "portable changing pad",
      "baby carrier",
      "baby bath tub",
      "baby bottle drying rack",
      "high chair",
      "baby gate",
      "white noise machine baby",
      "diaper caddy",
      "swaddle blankets",
      "baby food maker",
      "wipe warmer",
      "baby laundry hamper",
      "crib sheets",
      "baby travel crib",
      "pacifier holder",
      "baby closet dividers",
      "teething toys"
    ]
  }
];

export function categoryPackByKey(key: string): CategoryPack | undefined {
  return categoryPacks.find((pack) => pack.key === key);
}

export function profileQueryCount(profile: CollectionProfile): number {
  return collectionProfiles.find((item) => item.value === profile)?.queryCount ?? 12;
}
